import { useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { getImageUrl } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { alertError, alertSuccess, confirmDelete } from '../../utils/swal';
import AdminLayout from './AdminLayout';
import { EmptyState, LoadingState, PageHeader, TableToolbar } from './AdminUI';

const formatBytes = (bytes = 0) => {
    if (!bytes) return '0 KB';
    const units = ['bytes', 'KB', 'MB', 'GB'];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / (1024 ** index);
    return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const normalizeBrochure = (brochure) => {
    const name = brochure.name || brochure.filename || '';
    const url = brochure.url || brochure.fileUrl || '';

    return {
        ...brochure,
        id: name,
        name,
        title: brochure.title || name,
        fileUrl: getImageUrl(url) || url,
        sizeLabel: formatBytes(brochure.size),
        uploadedAtLabel: brochure.uploadedAt
            ? new Date(brochure.uploadedAt).toLocaleString()
            : 'Unknown'
    };
};

const BrochuresManagement = () => {
    const { apiCall } = useAuth();
    const [brochures, setBrochures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [tableSearch, setTableSearch] = useState('');
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        fetchBrochures();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredBrochures = useMemo(() => {
        const query = tableSearch.trim().toLowerCase();
        if (!query) return brochures;

        return brochures.filter((brochure) => [
            brochure.title,
            brochure.name,
            brochure.sizeLabel,
            brochure.uploadedAtLabel
        ].some((value) => String(value || '').toLowerCase().includes(query)));
    }, [brochures, tableSearch]);

    const fetchBrochures = async () => {
        try {
            const response = await apiCall('/brochures');
            const data = await response.json();
            const brochureList = Array.isArray(data.brochures) ? data.brochures :
                Array.isArray(data) ? data : [];
            setBrochures(brochureList.map(normalizeBrochure));
        } catch (error) {
            console.error('Error fetching brochures:', error);
            setBrochures([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedFile) {
            alertError('Missing file', 'Choose a PDF, DOC, or DOCX brochure to upload.');
            return;
        }

        const formData = new FormData();
        formData.append('brochure', selectedFile);

        try {
            setUploading(true);
            const response = await apiCall('/brochures', {
                method: 'POST',
                body: formData
            });

            const result = await response.json();

            if (response.ok) {
                await fetchBrochures();
                handleCloseModal();
                alertSuccess('Uploaded', 'Brochure uploaded successfully.');
            } else {
                alertError('Error', result.message || 'Error uploading brochure');
            }
        } catch (error) {
            console.error('Error uploading brochure:', error);
            alertError('Error', 'Error uploading brochure');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (filename) => {
        const { isConfirmed } = await confirmDelete({
            title: 'Delete brochure?',
            text: 'Are you sure? This cannot be undone.'
        });
        if (!isConfirmed) return;

        try {
            const response = await apiCall(`/brochures/${encodeURIComponent(filename)}`, { method: 'DELETE' });
            if (response.ok) {
                await fetchBrochures();
                alertSuccess('Deleted', 'Brochure deleted.');
            } else {
                const result = await response.json();
                alertError('Error', result.message || 'Error deleting brochure');
            }
        } catch (error) {
            console.error('Error deleting brochure:', error);
            alertError('Error', 'Error deleting brochure');
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedFile(null);
    };

    const brochureColumns = [
        {
            name: 'File',
            selector: (row) => row.title,
            sortable: true,
            cell: (row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                    <i className="fa fa-file-pdf-o" style={{ fontSize: '24px', color: '#ec4899' }}></i>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: '500', color: '#2d3748', overflowWrap: 'anywhere' }}>{row.title}</div>
                        <div style={{ fontSize: '12px', color: '#718096' }}>{row.sizeLabel}</div>
                    </div>
                </div>
            ),
            grow: 2
        },
        {
            name: 'Uploaded',
            selector: (row) => row.uploadedAt,
            sortable: true,
            cell: (row) => <span>{row.uploadedAtLabel}</span>,
            grow: 1
        },
        {
            name: 'Actions',
            cell: (row) => (
                <span style={{ display: 'flex', gap: '8px' }}>
                    <a
                        href={row.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            display: 'inline-block',
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '0',
                            cursor: 'pointer',
                            fontSize: '12px',
                            textDecoration: 'none'
                        }}
                        title="View"
                    >
                        <i className="fa fa-eye"></i>
                    </a>
                    <button
                        type="button"
                        onClick={() => handleDelete(row.name)}
                        style={{
                            background: '#e53e3e',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '0',
                            cursor: 'pointer',
                            fontSize: '12px'
                        }}
                        title="Delete"
                    >
                        <i className="fa fa-trash"></i>
                    </button>
                </span>
            ),
            width: '120px',
            ignoreRowClick: true
        }
    ];

    const tableCustomStyles = {
        headCells: {
            style: {
                padding: '12px 16px',
                fontWeight: '600',
                fontSize: '14px',
                color: '#4a5568',
                backgroundColor: '#f7fafc',
                borderBottom: '2px solid #e2e8f0'
            }
        },
        cells: {
            style: {
                padding: '12px 16px',
                fontSize: '14px',
                color: '#4a5568'
            }
        }
    };

    if (loading) {
        return (
            <AdminLayout>
                <LoadingState label="Loading brochures..." />
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="admin-page">
                <PageHeader
                    title="Brochures & Resources"
                    subtitle="Manage downloadable resources"
                    action={
                        <button
                            onClick={() => setShowModal(true)}
                            className="btn btn-primary"
                        >
                            <i className="fa fa-plus"></i>
                            Add Brochure
                        </button>
                    }
                />

                <div className="card admin-data-card">
                    <TableToolbar
                        search={tableSearch}
                        onSearchChange={setTableSearch}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={setRowsPerPage}
                        placeholder="Search brochures..."
                    />
                    <DataTable
                        columns={brochureColumns}
                        data={filteredBrochures}
                        customStyles={tableCustomStyles}
                        pagination
                        paginationPerPage={rowsPerPage}
                        paginationRowsPerPageOptions={[10, 20, 30]}
                        paginationComponentOptions={{ noRowsPerPage: true }}
                        progressPending={loading}
                        progressComponent={<div style={{ padding: '24px', color: '#718096' }}>Loading...</div>}
                        noDataComponent={
                            <EmptyState
                                icon="fa fa-file-pdf-o"
                                title="No brochures yet"
                                text="Upload your first brochure or resource to get started."
                            />
                        }
                        striped
                        dense
                    />
                </div>

                {showModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000
                    }}>
                        <div style={{
                            background: 'white',
                            borderRadius: '0',
                            maxWidth: '520px',
                            width: '90%',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            padding: '32px'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
                                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1a202c' }}>
                                    Add Brochure
                                </h2>
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#718096' }}
                                >
                                    <i className="fa fa-times"></i>
                                </button>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div style={{ marginBottom: '24px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                        Brochure file *
                                    </label>
                                    <input
                                        type="file"
                                        accept=".pdf,.doc,.docx"
                                        onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '0',
                                            fontSize: '14px'
                                        }}
                                    />
                                    <p style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                                        PDF, DOC, or DOCX. Maximum size follows the server upload limit.
                                    </p>
                                </div>

                                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        disabled={uploading}
                                        style={{
                                            padding: '12px 24px',
                                            background: '#e2e8f0',
                                            color: '#4a5568',
                                            border: 'none',
                                            borderRadius: '0',
                                            cursor: uploading ? 'not-allowed' : 'pointer',
                                            fontWeight: '500'
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={uploading}
                                        style={{
                                            padding: '12px 24px',
                                            background: uploading ? '#f9a8d4' : '#ec4899',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '0',
                                            cursor: uploading ? 'not-allowed' : 'pointer',
                                            fontWeight: '500'
                                        }}
                                    >
                                        {uploading ? 'Uploading...' : 'Upload Brochure'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default BrochuresManagement;
